// src/components/WebsiteExperimentEntry.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Button, Card, Spinner, Alert } from 'react-bootstrap';
import useToken from '../../hooks/useToken';
import useProgressRedirect from '../../hooks/useProgressRedirect';
import ProgressTracker from '../shared/ProgressTracker';

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const formatScheduledStart = (date) => {
  const month = date.toLocaleDateString(undefined, { month: 'long' });
  const day = ordinal(date.getDate());
  const year = date.getFullYear();
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return `${month} ${day}, ${year} at ${time}`;
};

const formatCountdown = (ms) => {
  const totalSecs = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

function WebsiteExperimentEntry() {
  const { getValidToken } = useToken();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scheduledStart, setScheduledStart] = useState(null);
  const [now, setNow] = useState(Date.now());
  const navigate = useNavigate();

  useProgressRedirect("EXPERIMENT");

  useEffect(() => {
    (async () => {
      try {
        const authToken = await getValidToken();
        const res = await fetch('/api/player/progress', {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = await res.json();
        if (data.scheduled_start_at) {
          setScheduledStart(new Date(data.scheduled_start_at));
        }
      } catch (err) {
        console.error('Failed to fetch schedule', err);
      }
    })();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const notYetStarted = scheduledStart && now < scheduledStart.getTime();
  const remaining = scheduledStart ? scheduledStart.getTime() - now : 0;

  const handleStartExperiment = async () => {
    setLoading(true);
    setError('');

    try {
      const authToken = await getValidToken();

      const assignmentRes = await fetch('/api/assignment', {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const assignmentData = await assignmentRes.json();

      if (!Array.isArray(assignmentData) || assignmentData.length === 0) {
        throw new Error('No assignments found');
      }

      const progressRes = await fetch('/api/player/progress', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ progress_step: 'DOCUMENTATION_REVIEW' }),
      });

      if (progressRes.ok) {
        navigate('/documentation-review', { replace: true });
      } else {
        const body = await progressRes.json();
        if (progressRes.status === 403 && body.scheduled_start_at) {
          setScheduledStart(new Date(body.scheduled_start_at));
          setError('The experiment has not started yet. Please wait for the countdown.');
        } else {
          setError(body.message || 'Failed to start the experiment.');
        }
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-5">
      <ProgressTracker currentStep="platform_tutorial" />
      <Card className="shadow p-4">
        <h2 className="mb-4">🧪 Ready to Begin the Experiment?</h2>
        <p>
          You are about to begin the practical part of the penetration testing study.
          After clicking the button below, your assignments will be prepared and
          you will be redirected to your first target application.
        </p>

        {notYetStarted && (
          <Alert variant="warning" className="text-center">
            <div className="mb-1">
              <strong>⏳ Experiment starts at:</strong>{' '}
              {formatScheduledStart(scheduledStart)}
            </div>
            <div style={{ fontSize: '2rem', fontVariantNumeric: 'tabular-nums', letterSpacing: 2 }}>
              {formatCountdown(remaining)}
            </div>
            <div className="mt-1 text-muted" style={{ fontSize: '0.85rem' }}>
              The button will unlock automatically when the time arrives.
            </div>
          </Alert>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="d-grid">
          <Button
            variant="success"
            onClick={handleStartExperiment}
            disabled={loading || notYetStarted}
          >
            {loading
              ? <><Spinner size="sm" animation="border" className="me-2" />Preparing Environment…</>
              : notYetStarted
                ? '🔒 Not yet available'
                : '🚀 Start the Experiment'}
          </Button>
        </div>
      </Card>
    </Container>
  );
}

export default WebsiteExperimentEntry;
